import { useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { SyncStatus, StoragePersistenceWarning } from './SyncStatus.js';
import { lock, lockState, onLockChange, shouldStartLocked, startIdleWatch, unlock } from '../lib/lock.js';
import { connect, type FieldEvent } from '../lib/realtime.js';
import { reference } from '../lib/localdb.js';
import { sync, wipeForRevoke } from '../lib/sync.js';
import { isReporting } from '../lib/geo.js';
import { BigButton } from './Controls.js';
import { NoticeBanner, NoticeGate, outstandingBanners, outstandingBlocking, useNotices } from '../screens/Notices.js';
import { shortTime } from '../lib/format.js';

/**
 * The frame.
 *
 * Three things live here because they are true of every screen: the sync state, the lock, and
 * whatever the office has just changed about the day.
 */
export function Shell({ children }: { children: ReactNode }) {
  const [locked, setLocked] = useState(false);
  const [banner, setBanner] = useState<Extract<FieldEvent, { kind: 'assignment' }> | null>(null);
  const [reporting, setReporting] = useState(false);
  const [prospecting, setProspecting] = useState(false);
  const [notices, reloadNotices] = useNotices();

  useEffect(() => {
    void (async () => {
      if (await shouldStartLocked()) {
        lock();
        setLocked(true);
      }
      const settings = await reference<{ lockAfterSeconds?: number; prospectingEnabled?: boolean }>('settings', {});
      setProspecting(Boolean(settings.prospectingEnabled));
      const stopWatch = startIdleWatch(settings.lockAfterSeconds ?? 900);
      return stopWatch;
    })();

    const unsubscribe = onLockChange((state) => setLocked(state === 'locked'));

    /*
     * The socket, and what it is for.
     *
     * A stop added at 10:02 for 10:30 cannot wait for a five-minute heartbeat. Anything that
     * names this technician's own day raises a banner and triggers a sync; nothing changes
     * underneath them silently.
     */
    const disconnect = connect((event) => {
      if (event.kind === 'revoked') {
        // The office took this phone away. Everything local goes, whatever it was holding.
        void wipeForRevoke().then(() => window.location.reload());
        return;
      }
      if (event.kind === 'assignment') {
        setBanner(event);
        void sync();
        return;
      }
      if (event.entity === 'appointment' || event.entity === 'hazard') void sync();
    });

    const ticker = window.setInterval(() => setReporting(isReporting()), 5000);

    return () => {
      unsubscribe();
      disconnect();
      window.clearInterval(ticker);
    };
  }, []);

  if (locked) return <LockScreen onUnlock={() => void unlock()} />;

  /*
   * A blocking notice comes before the day, and before the day's data.
   *
   * Deliberately after the lock and before everything else: whoever is holding the phone has
   * been identified, and has not yet seen a customer's address. The terms of use and the Law 25
   * geolocation notice both belong exactly here.
   */
  const blocking = outstandingBlocking(notices);
  if (blocking.length > 0) return <NoticeGate notices={blocking} onDone={reloadNotices} />;

  const banners = outstandingBanners(notices);

  return (
    <div className="shell">
      <div className="shell__bar">
        <SyncStatus />
        {/* Always visible while position is being reported. Never quietly on. */}
        {reporting && (
          <span className="shell__position" title="Position transmise à la répartition">
            ◉ Position
          </span>
        )}
        <nav className="shell__nav">
          {prospecting && (
            <Link to="/observation" aria-label="Nouvelle observation">
              ⌖
            </Link>
          )}
          <Link to="/appareil" aria-label="Cet appareil">
            ⚙
          </Link>
        </nav>
      </div>

      {banner && <ChangeBanner event={banner} onAcknowledge={() => setBanner(null)} />}

      {/* Recalls, internal rules, the recurring issue everybody keeps hitting. Never in the way. */}
      {banners.map((notice) => (
        <NoticeBanner key={notice.versionId} notice={notice} onAcknowledged={reloadNotices} />
      ))}

      <StoragePersistenceWarning />

      <main className="shell__main">{children}</main>
    </div>
  );
}

/**
 * A change to the day, acknowledged rather than dismissed.
 *
 * Dispatch sees *vu par Marc 10:22* rather than wondering whether the message landed, which is
 * the difference between a notification and a change nobody noticed.
 */
function ChangeBanner({ event, onAcknowledge }: { event: Extract<FieldEvent, { kind: 'assignment' }>; onAcknowledge: () => void }) {
  const what =
    event.change === 'added'
      ? `Un arrêt a été ajouté${event.startsAt ? ` à ${shortTime(event.startsAt)}` : ''}`
      : event.change === 'cancelled'
        ? 'Un arrêt a été annulé'
        : event.change === 'moved'
          ? `Un arrêt a été déplacé${event.startsAt ? ` à ${shortTime(event.startsAt)}` : ''}`
          : 'Une note a été ajoutée à un arrêt';

  return (
    <div className="banner" role="status">
      <p>
        {what}
        {event.customerName ? ` — ${event.customerName}` : ''}
      </p>
      <button type="button" onClick={onAcknowledge}>
        Vu
      </button>
    </div>
  );
}

/**
 * The lock.
 *
 * Sessions are 180 days long so a technician in a basement never has to sign in; this is what
 * makes that length acceptable. It says why it exists, because a lock nobody understands is a
 * lock somebody turns off.
 */
function LockScreen({ onUnlock }: { onUnlock: () => void }) {
  useEffect(() => {
    void lockState();
  }, []);

  return (
    <div className="lockscreen">
      <h1>Verrouillé</h1>
      <p>Cet appareil contient les coordonnées et les codes d’accès de clients.</p>
      <BigButton onClick={onUnlock}>Déverrouiller</BigButton>
    </div>
  );
}
